// ResponsibilityChainEditor — the ordered lead→backup accountability chain for
// ONE responsibility at ONE scope (ADR-026 §C). Level-agnostic + wire-agnostic:
// it edits an ordered list of people ids via props. The zones feature was its
// first consumer; cluster / site / team / vehicle-pool / warehouse and the
// user-profile side reuse it unchanged (the chain shape is identical at every
// level — only the scope it's mounted on differs).
//
// The model is two orthogonal controls over ONE chain:
//   • MEMBERSHIP — the family's picker (ADR-080) over the host's SOURCE — the
//                  person lookup's `accountable` rule of the place (ADR-078 §6):
//                  A to Z by search, 20 at a time, "Will be granted scope" on a
//                  row whose scope does not reach the place yet, a disabled
//                  person greyed once typed. Its closed face shows NOTHING of the
//                  chosen (`selectedDisplay="none"` — ADR-080 §10: an ordered
//                  chain shows its chosen in its own ordered list, one ✕ per
//                  person, no count, no clear-all); opened, the chosen lead it
//                  under "Selected" so a person can be un-ticked there too.
//   • ORDER      — the SortableList drags lead→backup (display_order); position 1
//                  is the lead, the rest are backups (the escalation chain).
// Removing is reachable from either (the picker's tick or a row's ✕). Order is
// preserved across membership edits (kept rows stay put; new picks append).
//
// Presentational: it never calls the API — the HOST names the chain's people
// (`people`: the lookup's rows of given ids, mapped to `ChainPerson`; a person
// not named yet reads "…") and takes its domain strings as props (like
// SortableList/DataTable), so the Workbench story drives it with a vocabulary
// source + bilingual fixtures and the live page passes the lookup source, t()
// values and a commit handler. The container (a SlideOver on the place's page /
// peek) owns title, Save/Cancel, and the PUT of the whole ordered id set (the
// service renumbers display_order 1..N).

import { Avatar } from '../ui/Avatar.js';
import { Badge } from '../ui/Badge.js';
import { SortableList } from '../ui/SortableList.js';
import { icons } from '../ui/icons.js';
import type { CodeSource, ServerSource, SmallSource } from '../../hooks/useOptions.js';
import { Picker } from './Picker.js';

/** One person of the chain, as the host NAMED them. `subtitle` is their role (· their status word). */
export interface ChainPerson {
  id: string;
  name: string;
  subtitle?: string;
  /**
   * ADR-052 §5 proactive surfacing — naming this person will require a companion scope
   * grant (their own scope doesn't yet reach this responsibility's scope). PROP-DRIVEN:
   * the editor renders a "needs scope" chip beside them when true; it never computes this
   * itself (the host maps it from the lookup row's `covered`). Default false/absent.
   */
  needsScope?: boolean;
  /** A disabled holder — kept in the chain, greyed, removable (a save checks only the people it adds). */
  inactive?: boolean;
}

export interface ResponsibilityChainEditorLabels {
  /** The picker's field label, e.g. "Assignees". */
  picker: string;
  /** The picker's placeholder, e.g. "Add people…". */
  pickerPlaceholder: string;
  /** aria-label for the ordered list, e.g. "Zone Manager assignees, ordered". */
  listLabel: string;
  /** Position-1 tag, e.g. "Lead". */
  lead: string;
  /** Position 2+ tag, e.g. "Backup". */
  backup: string;
  /** Empty state, e.g. "No one assigned yet". */
  empty: string;
  /** Per-row remove button aria-label; receives the person's name. */
  remove: (name: string) => string;
  /** Per-row drag handle aria-label; receives the person's name. */
  reorder: (name: string) => string;
  /**
   * ADR-052 §5 — the "needs scope grant" chip's label, shown beside a
   * person whose `needsScope` is true. Optional: when absent the chip is suppressed (the
   * editor degrades to no marker), so non-scope consumers need not supply it.
   */
  needsScope?: { chip: string };
}

export interface ResponsibilityChainEditorProps {
  /** The current ordered chain's ids (index 0 = lead). */
  value: readonly string[];
  /** Emits the next ordered ids on any add / remove / reorder. */
  onChange: (next: string[]) => void;
  /** The chain's people, named by the host (the person lookup's rows of given ids); an id not named yet reads "…". */
  people: ReadonlyMap<string, ChainPerson>;
  /** The picker's source — the person lookup's `accountable` rule of the place; a story gives a vocabulary. */
  source: CodeSource | SmallSource | ServerSource;
  /** The face: `edit` (the picker, ✕, drag) · `remove` — a closed place's chain takes only a removal (ADR-080 §5): ✕
   *  alone, no picker, no drag · `read` — the viewer can't assign at this level (no picker, no ✕, no drag). */
  mode?: 'edit' | 'remove' | 'read';
  labels: ResponsibilityChainEditorLabels;
}

const UNNAMED = '…';

export function ResponsibilityChainEditor({ value, onChange, people, source, mode = 'edit', labels }: ResponsibilityChainEditorProps) {
  const chain: ChainPerson[] = value.map((id) => people.get(id) ?? { id, name: UNNAMED });

  // Reconcile a membership change: keep existing chain rows in place (preserves
  // order), append newly-picked ids in pick order, drop unticked ids.
  function setMembership(nextIds: string[]) {
    const kept = value.filter((id) => nextIds.includes(id));
    const added = nextIds.filter((id) => !value.includes(id));
    onChange([...kept, ...added]);
  }

  return (
    <div className="flex flex-col gap-3">
      {mode === 'edit' && (
        <Picker
          multiple
          selectedDisplay="none"
          source={source}
          label={labels.picker}
          placeholder={labels.pickerPlaceholder}
          value={[...value]}
          onChange={setMembership}
        />
      )}

      {chain.length === 0 ? (
        <p className="rounded-md border border-dashed border-[var(--color-border)] px-3 py-4 text-center text-sm text-[var(--color-text-secondary)]">
          {labels.empty}
        </p>
      ) : mode === 'edit' ? (
        <SortableList
          items={chain}
          getId={(p) => p.id}
          onReorder={(next) => onChange(next.map((p) => p.id))}
          ariaLabel={labels.listLabel}
          handleLabel={(p) => labels.reorder(p.name)}
          renderItem={(p) => (
            <ChainRow
              person={p}
              position={chain.findIndex((c) => c.id === p.id)}
              labels={labels}
              onRemove={() => onChange(value.filter((id) => id !== p.id))}
            />
          )}
        />
      ) : (
        // Remove only · read only: a plain ordered list, the same row layout minus the handle (and minus ✕ when read).
        <ul aria-label={labels.listLabel} className="flex flex-col gap-1">
          {chain.map((p, i) => (
            <li
              key={p.id}
              className="flex items-center gap-2 rounded-md border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-3 py-2"
            >
              <ChainRow person={p} position={i} labels={labels} onRemove={mode === 'remove' ? () => onChange(value.filter((id) => id !== p.id)) : undefined} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// One chain row's CONTENT (position tag · avatar · name/subtitle · optional ✕).
// In the sortable case SortableList supplies the <li> + drag handle and slots
// this in; in read-only mode the parent supplies a plain <li>.
function ChainRow({
  person,
  position,
  labels,
  onRemove,
}: {
  person: ChainPerson;
  position: number;
  labels: ResponsibilityChainEditorLabels;
  onRemove?: () => void;
}) {
  const isLead = position === 0;
  // ADR-052 §5 — the "needs scope grant" chip, shown only when the person is flagged AND
  // the label is supplied (prop-driven). The chip's four words say it — nothing hides in a hover.
  const showNeedsScope = person.needsScope === true && labels.needsScope !== undefined;
  // A disabled holder: greyed, its status word on the subtitle (the face's) — kept and removable.
  const nameClass = person.inactive ? 'text-[var(--color-text-secondary)]' : 'text-[var(--color-text-primary)]';
  return (
    <div className="flex flex-1 items-center gap-3 min-w-0">
      <span className="tabular-nums text-xs text-[var(--color-text-secondary)] w-4 text-end shrink-0">
        {position + 1}
      </span>
      <Avatar name={person.name} size="sm" />
      <span className="flex flex-col min-w-0">
        <span className={`truncate text-sm font-medium ${nameClass}`}>{person.name}</span>
        {person.subtitle && (
          <span className="truncate text-xs text-[var(--color-text-secondary)]">
            {person.subtitle}
          </span>
        )}
      </span>
      {showNeedsScope && labels.needsScope && (
        <Badge variant="warning" appearance="subtle" className="ms-auto shrink-0">
          <icons.statusWarning aria-hidden size={12} />
          {labels.needsScope.chip}
        </Badge>
      )}
      <Badge variant={isLead ? 'brand' : 'neutral'} className={`${showNeedsScope ? '' : 'ms-auto'} shrink-0`}>
        {isLead ? labels.lead : labels.backup}
      </Badge>
      {onRemove && (
        <button
          type="button"
          aria-label={labels.remove(person.name)}
          onClick={onRemove}
          className="shrink-0 inline-flex items-center justify-center rounded-md p-1.5 min-w-[1.75rem] min-h-[1.75rem] text-[var(--color-text-secondary)] hover:text-[var(--color-text-primary)] hover:bg-[var(--color-bg-hover)] outline-none focus-ring-inset cursor-pointer"
        >
          <icons.close aria-hidden size={14} />
        </button>
      )}
    </div>
  );
}
